import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
//import { useDispatch } from 'react-redux';
import {
  Flex,
  Stack,
  useColorModeValue,
  Button,
  Text,
  Box,
  Image,
  Skeleton,
  SkeletonCircle,
  //IconButton
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import { faArrowLeft, faRotate } from '@fortawesome/free-solid-svg-icons';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import Reward from '../../components/UserComponents/Reward';
import RedeemedHistory from '../../components/UserComponents/RedeemedHistory';
//import Loader from '../../components/Loader';
import {
  useGetUserRedeemedHistoryMutation,
  useGetUserDataMutation,
  useGetActiveRewardsDataMutation,
} from '../../slices/userApiSlice';
import { useTranslation } from 'react-i18next';
import dateUtils from '../../utils/formatDate';
import MonthYearPicker from '../../components/UserComponents/MonthYearPicker';

const UserRewardScreen = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const {t} = useTranslation();
  const savedLanguage = localStorage.getItem("i18nextLng") || "en";

  // API Mutations
  const [getRewards] = useGetActiveRewardsDataMutation();
  const [activeRewardsLoading, setActiveRewardsLoading] = useState(false);

  const [getUserData] = useGetUserDataMutation();
  //const [loading, setLoading] = useState(false);

  const [getUserRedeemedHistory] = useGetUserRedeemedHistoryMutation();
  const [userRewardRedeemedHistoryLoading, setUserRewardRedeemedHistoryLoading] = useState(false);

  // State
  const [redeemedHistory, setRedeemedHistory] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [userData, setUserData] = useState(null);
  const [view, setView] = useState('reward');
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchPoints = userData?.current_points ?? 0;

  // Theme Colors
  const bgColor = useColorModeValue('teal.600', 'gray.800');
  const bgColorDark = useColorModeValue('gray.50', 'gray.700');
  const textColor = useColorModeValue('custom.900', 'gray.200');
  const navBgColor = useColorModeValue('custom.900', 'gray.800');
  const selectedButtonBgColor = useColorModeValue('teal.500', 'teal.300');
  const selectedButtonColor = useColorModeValue('custom.800', 'gray.100');
  //const pointBoxBgColor = useColorModeValue('white', 'gray.700');
  const pointTextColor = useColorModeValue('teal.600', 'teal.200');
  //const bgColorIcon = useColorModeValue('rgba(0, 0, 0, 0.2)', 'rgba(255, 255, 255, 0.2)');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const userResponse = await getUserData(id).unwrap();
      setUserData(userResponse.user);
    } catch(err) {
        // console.error('Error fetching user data:', err);
    } finally {
      setLoading(false);
    }
  }, [id, getUserData]);

  // const handleFetchData = async () => {
  //   await fetchData();
  // };

  const fetchUserRewardRedeemedHistory = useCallback(async () => {
    if (!id || !selectedMonth || !selectedYear) {
      return;
    }
    setUserRewardRedeemedHistoryLoading(true);
    try {
      const redeemedResponse = await getUserRedeemedHistory({
        id,
        selectedMonth,
        selectedYear,
      }).unwrap();
      setRedeemedHistory(redeemedResponse?.reward_history || []);
  
    } catch (err) {
      // console.error('Error fetching points history:', err);
      // Optionally, set an error state or show a notification to the user
    } finally {
      setUserRewardRedeemedHistoryLoading(false);
    }
  }, [id, selectedMonth, selectedYear, getUserRedeemedHistory]);

  const fetchActiveReward = useCallback(async () => {
    setActiveRewardsLoading(true);
    try {
      const rewardsResponse = await getRewards();
      setRewards(rewardsResponse.data);
  
    } catch (err) {
      // console.error('Error fetching points history:', err);
      // Optionally, set an error state or show a notification to the user
    } finally {
      setActiveRewardsLoading(false);
    }
  }, [getRewards]);

  useEffect(() => {
    fetchData();
    fetchUserRewardRedeemedHistory();
    fetchActiveReward();
  }, [fetchData, fetchUserRewardRedeemedHistory, fetchActiveReward]);

  const handleDateChange = (month, year) => {
    setSelectedMonth(month);
    setSelectedYear(year);
  };

  // Function to handle modal close and trigger fetchData
  const handleModalClose = () => {
    //setIsModalOpen(false);
    fetchData(); // Re-fetch data when modal closes
  };

  // Inline style for the rotating animation
  // const rotatingStyle = {
  //   display: 'inline-block',
  //   cursor: 'pointer',
  //   color: "white",
  //   transform: 'rotate(140deg)',
  //   animation: loading ? 'rotate 1.5s linear infinite' : 'none',
  // };
  
  // // Inline CSS for keyframes
  // const keyframes = `
  //   @keyframes rotate {
  //     0% { transform: rotate(0deg); }
  //     100% { transform: rotate(360deg); }
  //   }
  // `;

  return (
    <Flex
      minH="100vh"
      direction="column"
      align="center"
      justify="flex-start"
      bg={bgColor}
      color={textColor}
      w="100%"
      pt={`calc(1rem + env(safe-area-inset-top))`}
    >
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        w="100%"
        p={2}
        mb={4}
        bg={bgColor}
      >
        <FontAwesomeIcon
          icon={faArrowLeft}
          size="xl"
          onClick={() => navigate('/')}
          style={{ cursor: 'pointer', marginRight: '8px' }}
        />
        <Text fontSize="xl" fontWeight="bold" ml={2}>
          {t('menu.items.reward_history')}
        </Text>
      </Box>

      {/* Main Content */}
      <Stack
        w="full"
        maxW="lg"
        borderRadius="2xl"
        bg={bgColorDark}
        p={3}
        position="relative"
        spacing={6}
        flexGrow={1}
      >
        {/* Total Points Floating Box */}
        <Box
          position="absolute"
          top="0.5rem"
          left="50%"
          transform="translateX(-50%)"
          px={6}
          py={3}
          w="full"
          maxW={{ base: 'sm', md: 'md' }}
          borderRadius="xl"
          bg={bgColorDark}
          zIndex={10}
        >
          <Stack direction="row" spacing={3} align="center" justify="center">
            <Text fontSize="md" color={pointTextColor} fontWeight="bold">
              {t('points')}
            </Text>

            {loading ? (
              <>
                <SkeletonCircle size="30px" />
                <Skeleton height="28px" width="50px" />
              </>
            ) : (
              <>
                <Image src="/star.png" boxSize="30px" alt="Points Icon" />
                <Text fontSize="2xl" fontWeight="bold" color={selectedButtonColor}>
                  {dateUtils.convertToBurmeseNumerals(fetchPoints || 0, savedLanguage)}
                </Text>
              </>
            )}

            {/* <IconButton
              aria-label="Refresh Points"
              icon={
                <>
                  <style>{keyframes}</style>
                  <FontAwesomeIcon icon={faRotate} style={rotatingStyle} />
                </>
              }
              onClick={handleFetchData}
              size="sm"
              bg={bgColorIcon}
              _hover={{ bg: 'teal.500', color: 'white' }}
              borderRadius="full"
            /> */}
          </Stack>
        </Box>

        {/* Navigation Buttons */}
        <Stack
          direction="row"
          spacing={4}
          mt={14}
          w="full"
          bg={navBgColor}
          rounded="full"
          p={1}
          boxShadow="md"
        >
          <Button
            w="full"
            rounded="full"
            bg={view === 'reward' ? selectedButtonBgColor : navBgColor}
            color={view === 'reward' ? navBgColor : selectedButtonColor}
            onClick={() => setView('reward')}
            _hover={{ color: 'white', bg: 'teal.500' }}
          >
            {t('rewards')}
          </Button>
          <Button
            w="full"
            rounded="full"
            bg={view === 'history' ? selectedButtonBgColor : navBgColor}
            color={view === 'history' ? navBgColor : selectedButtonColor}
            onClick={() => setView('history')}
            _hover={{ color: 'white', bg: 'teal.500' }}
          >
            {t('menu.sections.history')}
          </Button>
        </Stack>

        {/* Main Content Area */}
        {view === 'reward' ? (
          rewards?.length > 0 ? (
            <Reward
              rewards={rewards}
              userRewards={userData}
              isLoading={activeRewardsLoading}
              onModalClose={handleModalClose}
            />
          ) : (
            <Text fontSize="sm" textAlign="center" color="gray.500">
              {t('no_data_with_date', {
                date: dateUtils.formatDateDisplay(selectedYear, selectedMonth, savedLanguage),
              })}
            </Text>
          )
        ) : (
          <RedeemedHistory
            redeemedRewards={redeemedHistory}
            isLoading={userRewardRedeemedHistoryLoading}
            selectedYear={selectedYear}
            selectedMonth={selectedMonth}
          >
            <MonthYearPicker onChange={handleDateChange} language={savedLanguage} />
          </RedeemedHistory>
        )}
      </Stack>
    </Flex>
  );  
};

export default UserRewardScreen;